import React from 'react';

import {
	Stack,
	useDisclosure,
	IconButton,
	Drawer,
	DrawerCloseButton,
	DrawerContent,
	DrawerHeader,
	DrawerBody,
	DrawerOverlay,
} from '@chakra-ui/react';
import { cn } from '@easydatasolution/ui-react/lib/utils';
import { FaBars } from '@react-icons/all-files/fa/FaBars';
import { FaEnvelope } from '@react-icons/all-files/fa/FaEnvelope';
import { FaMapMarkerAlt } from '@react-icons/all-files/fa/FaMapMarkerAlt';
import { FaPhone } from '@react-icons/all-files/fa/FaPhone';
import { motion, useTransform } from 'framer-motion';
import { Link } from 'gatsby';
import { useMeasure } from 'react-use';

import { NavigationMobileItems } from './NavigationMobileItems/NavigationMobileItems';
import { useDirectionalScrollProgress } from '../../../hooks/use-directional-scroll-progress';
import { useSiteMetadata } from '../../../hooks/use-site-metadata';
import { Url } from '../../../misc/enums';
import { formatPhoneNumber } from '../../../misc/utilities';
import { ContactButton } from '../../ContactButton/ContactButton';
import { CartIcon } from '../CartIcon/CartIcon';

const scrollInputRange = [0, 40];

interface NavigationMobileProps {
	className?: string;
}

export const NavigationMobile: React.FC<NavigationMobileProps> = ({ className }) => {
	const {
		organization: {
			contactPoint: { telephone, email },
		},
	} = useSiteMetadata();
	const [mobileNavRef, { height: headerHeight }] = useMeasure<HTMLDivElement>();
	const pixelsScrolled = useDirectionalScrollProgress({
		inputRange: scrollInputRange,
		threshold: { down: headerHeight },
	});
	const barsOpacity = useTransform(pixelsScrolled, scrollInputRange, [1, 0.6]);
	// 24 is from py-3 which is not part of the headerHeight 🤷🏼‍♂️
	const barsTranslateY = useTransform(pixelsScrolled, scrollInputRange, [0, -(headerHeight + 24)]);

	const { isOpen, onOpen, onClose } = useDisclosure();
	const btnRef = React.useRef<HTMLButtonElement>(null);

	return (
		<motion.div
			className={cn(['flex items-center justify-between bg-secondary-700 px-4 py-3', className])}
			style={{ opacity: barsOpacity, translateY: barsTranslateY }}
			ref={mobileNavRef}
		>
			<Stack isInline alignItems="center" spacing={5}>
				<ContactButton
					href={`tel:${formatPhoneNumber(telephone)}`}
					aria-label="Telefonnummer"
					icon={<FaPhone />}
				/>
				<ContactButton
					as="a"
					href={`mailto:${email}`}
					aria-label="E-Mail Adresse"
					icon={<FaEnvelope />}
				/>
				<ContactButton
					as="a"
					href="https://g.page/easy-data-solution-gmbh?share"
					rel="noopener noreferrer nofollow"
					target="_blank"
					aria-label="Adresse Google Maps"
					icon={<FaMapMarkerAlt />}
					mr="auto"
				/>
			</Stack>
			<Stack isInline alignItems="center" spacing={5}>
				<Link to={`/${Url.CART}/`}>
					<CartIcon className="text-white" />
				</Link>
				<IconButton
					ref={btnRef}
					bg="secondary.50"
					aria-label="Navigation öffnen"
					fontSize="20px"
					icon={<FaBars />}
					onClick={onOpen}
					color="secondary.700"
				/>
			</Stack>
			<Drawer
				isOpen={isOpen}
				placement="right"
				preserveScrollBarGap={true}
				onClose={onClose}
				finalFocusRef={btnRef}
			>
				<DrawerOverlay />
				<DrawerContent>
					<DrawerCloseButton />
					<DrawerHeader as={Link} to="/">
						easy data solution
					</DrawerHeader>
					<DrawerBody as="nav" position="relative" overflowX="hidden">
						<NavigationMobileItems />
					</DrawerBody>
				</DrawerContent>
			</Drawer>
		</motion.div>
	);
};
